import type { DataProvider, HttpError } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import i18n from '../i18n';
import { graphqlErrorInfo } from '../utils/graphqlErrors';

/**
 * F-177 — one place where a failed request becomes something a therapist can read.
 *
 * `graphql-request` throws a `ClientError` whose `message` is the server's sentence FOLLOWED BY a JSON
 * dump of the whole response and the whole request (the query text and its variables, a client's id
 * included). Refine's default error notification prints `message` verbatim, so a refused query showed
 * `You do not have permissions …: {"response":{"errors":[…]},"request":{"query":"…"}}`.
 *
 * `normalizeDataError` runs at the data-provider boundary (`withNormalizedErrors`), so every
 * useList / useOne / useCustom / useCreate / … — with or without `errorNotification: false` — throws a
 * Refine `HttpError` instead:
 *
 *   message     a translated, actionable sentence (`requestErrors.*`) chosen by `extensions.code`;
 *               a CODED refusal the server already localised by `Accept-Language` (HOMEWORK_DUE_IN_PAST,
 *               ES_MODE_MISMATCH, …) keeps ITS sentence — it is the message, the screens show it
 *               verbatim. Never JSON, never the query.
 *   statusCode  the HTTP status when the server sent one, else the code's usual one.
 *   code        `extensions.code` — components that branch on the code keep working.
 *   errors      `[{ message, extensions: { code } }]` — the shape `graphqlErrorInfo` reads, so every
 *               screen that maps codes itself receives the same code and the server's own sentence.
 *   original    the untouched error, NON-ENUMERABLE (not serialised, not scanned) — for Sentry, the
 *               console and the two auth checks that match the server's text (`rawErrorMessage`).
 *
 * It sets no `status`: the auth provider's `error.status === 401` sign-out is exactly as before.
 */
type Translate = (key: string) => string;

type Gql = { message?: string; extensions?: { code?: string } };

const KEY = {
  notAuthorized: 'requestErrors.notAuthorized',
  unauthenticated: 'requestErrors.unauthenticated',
  forbidden: 'requestErrors.forbidden',
  notFound: 'requestErrors.notFound',
  badInput: 'requestErrors.badInput',
  network: 'requestErrors.network',
  generic: 'requestErrors.generic',
} as const;

type Kind = keyof typeof KEY;

const KIND_BY_CODE: Record<string, Kind> = {
  USER_NOT_AUTHORIZED: 'notAuthorized',
  UNAUTHENTICATED: 'unauthenticated',
  FORBIDDEN: 'forbidden',
  NOT_FOUND: 'notFound',
  BAD_USER_INPUT: 'badInput',
  // the server's own failure words carry internals ("Cannot query field", a driver's text) — never shown
  INTERNAL_SERVER_ERROR: 'generic',
  DB_ERROR: 'generic',
  ERROR: 'generic',
  GRAPHQL_VALIDATION_FAILED: 'generic',
  GRAPHQL_PARSE_FAILED: 'generic',
};

const STATUS_BY_KIND: Record<Kind, number> = {
  notAuthorized: 403,
  unauthenticated: 401,
  forbidden: 403,
  notFound: 404,
  badInput: 400,
  network: 503,
  generic: 500,
};

/** A server sentence that is not fit to show: JSON, a query, or a stack. */
const LEAKS = /[{}]|"query"|"response"|\bquery\s*[({]|GraphQL|\bat\s+\S+\s+\(|\.(?:ts|js):\d+/;

const isNetworkError = (error: unknown): boolean => {
  const e = error as { response?: unknown; name?: string; message?: string } | null;
  if (e?.response) return false;
  return (
    e instanceof TypeError ||
    e?.name === 'TypeError' ||
    /failed to fetch|networkerror|load failed|network request failed|econnrefused/i.test(e?.message ?? '')
  );
};

export type NormalizedDataError = Omit<HttpError, 'errors'> & {
  code?: string;
  errors: Gql[];
  original: unknown;
};

/** Already normalised — normalising twice must be a no-op (a provider call made from inside a wrapped one). */
const isNormalized = (error: unknown): error is NormalizedDataError =>
  !!error && typeof error === 'object' && 'original' in (error as object) && 'statusCode' in (error as object);

export const normalizeDataError = (
  error: unknown,
  t: Translate = (key) => i18n.t(key) as string,
): NormalizedDataError => {
  if (isNormalized(error)) return error;

  const { code, message: serverMessage } = graphqlErrorInfo(error);
  const httpStatus = (error as { response?: { status?: number } } | null)?.response?.status;

  let kind: Kind;
  let message: string;
  let statusCode: number;

  if (code && KIND_BY_CODE[code]) {
    kind = KIND_BY_CODE[code];
    message = t(KEY[kind]);
    statusCode = STATUS_BY_KIND[kind];
  } else if (code && serverMessage && !LEAKS.test(serverMessage)) {
    // a coded, already-localised refusal of a feature (HOMEWORK_DUE_IN_PAST, ES_MODE_MISMATCH, …): its sentence is the message
    kind = 'generic';
    message = serverMessage;
    statusCode = httpStatus && httpStatus >= 400 ? httpStatus : 422;
  } else if (!code && isNetworkError(error)) {
    kind = 'network';
    message = t(KEY.network);
    statusCode = STATUS_BY_KIND.network;
  } else if (!code && httpStatus === 401) {
    kind = 'unauthenticated';
    message = t(KEY.unauthenticated);
    statusCode = 401;
  } else if (!code && httpStatus === 403) {
    kind = 'forbidden';
    message = t(KEY.forbidden);
    statusCode = 403;
  } else {
    kind = 'generic';
    message = t(KEY.generic);
    statusCode = httpStatus && httpStatus >= 400 ? httpStatus : STATUS_BY_KIND.generic;
  }

  const out = new Error(message) as unknown as NormalizedDataError;
  out.message = message;
  out.statusCode = statusCode;
  out.code = code;
  out.errors = code ? [{ message: serverMessage ?? message, extensions: { code } }] : [];
  Object.defineProperty(out, 'original', { value: error, enumerable: false });
  // `kind` is not part of the contract; it only decides what is reported below
  Object.defineProperty(out, 'kind', { value: kind, enumerable: false });
  return out;
};

/**
 * The server's own text, untouched — for the two places that match it (a first Google sign-in has no
 * database user yet: "You do not have permissions"; an expired token: `auth/id-token-expired`).
 */
export const rawErrorMessage = (error: unknown): string => {
  const original = (error as { original?: { message?: string } } | null)?.original;
  return (original?.message ?? (error as { message?: string } | null)?.message ?? '') as string;
};

/** Sentry sees what a therapist can neither read nor fix: the network, the server, an uncoded failure. */
const reportable = (n: NormalizedDataError) => {
  const kind = (n as unknown as { kind?: Kind }).kind;
  return kind === 'network' || kind === 'generic';
};

const WRAPPED: (keyof DataProvider)[] = [
  'getList',
  'getMany',
  'getOne',
  'create',
  'createMany',
  'update',
  'updateMany',
  'deleteOne',
  'deleteMany',
  'custom',
];

/** Wrap a Refine data provider: every rejection leaves it as a `NormalizedDataError`. */
export const withNormalizedErrors = (provider: Required<DataProvider>): Required<DataProvider> => {
  const wrapped = { ...provider } as Record<string, unknown>;
  for (const method of WRAPPED) {
    const original = provider[method] as ((...a: unknown[]) => Promise<unknown>) | undefined;
    if (typeof original !== 'function') continue;
    wrapped[method] = async (...args: unknown[]) => {
      try {
        return await original.apply(provider, args);
      } catch (error) {
        const normalized = normalizeDataError(error);
        console.warn(`[portal] ${String(method)} failed`, error);
        if (reportable(normalized)) Sentry.captureException(normalized.original);
        throw normalized;
      }
    };
  }
  return wrapped as unknown as Required<DataProvider>;
};
